// src/online/match-link.ts
// Async share-link multiplayer: encode/decode a full match (config + action log)
// to a compact base64url token that lives in the URL.
//
// Schema v1 (little-endian):
//   u8   version (1)
//   u32  seed
//   u8   matchTarget    (1..5)
//   u8   playerCount    (2..4)
//   for each player:
//     u8 nameLen
//     <nameLen> bytes utf8 (max 14 chars)
//   u16  actionCount
//   for each action (4 bytes):
//     u8  weaponIdx (0..33)
//     u8  angleDeg  (0..180)
//     u16 power     (0..1000)

import { WEAPONS } from '../sim/weapons';

export const MATCH_LINK_VERSION = 1;

export interface MatchAction {
  weaponIdx: number;   // index into WEAPONS
  angleDeg: number;    // 0..180 (UI convention)
  power: number;       // 0..1000
}

export interface MatchData {
  version: number;
  seed: number;
  matchTarget: number;
  playerCount: number;
  names: string[];
  actions: MatchAction[];
}

const NAME_MAX = 14;
const enc = new TextEncoder();
const dec = new TextDecoder();

export function encodeMatch(m: MatchData): string {
  // Compute size first
  const namesBytes = m.names.slice(0, m.playerCount).map(n => enc.encode(n.slice(0, NAME_MAX)));
  let size = 1 + 4 + 1 + 1; // version, seed, target, count
  for (const nb of namesBytes) size += 1 + nb.length;
  size += 2; // actionCount
  size += m.actions.length * 4;

  const buf = new Uint8Array(size);
  const view = new DataView(buf.buffer);
  let o = 0;
  buf[o++] = m.version;
  view.setUint32(o, m.seed >>> 0, true); o += 4;
  buf[o++] = m.matchTarget;
  buf[o++] = m.playerCount;
  for (const nb of namesBytes) {
    buf[o++] = nb.length;
    buf.set(nb, o); o += nb.length;
  }
  view.setUint16(o, m.actions.length, true); o += 2;
  for (const a of m.actions) {
    buf[o++] = a.weaponIdx & 0xff;
    buf[o++] = a.angleDeg & 0xff;
    view.setUint16(o, a.power & 0xffff, true); o += 2;
  }
  return bytesToBase64Url(buf);
}

export function decodeMatch(token: string): MatchData | null {
  let buf: Uint8Array;
  try {
    buf = base64UrlToBytes(token);
  } catch {
    return null;
  }
  if (buf.length < 7) return null;
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let o = 0;
  const version = buf[o++];
  if (version !== MATCH_LINK_VERSION) return null;
  const seed = view.getUint32(o, true); o += 4;
  const matchTarget = buf[o++];
  const playerCount = buf[o++];
  if (playerCount < 2 || playerCount > 4) return null;
  const names: string[] = [];
  for (let p = 0; p < playerCount; p++) {
    if (o >= buf.length) return null;
    const len = buf[o++];
    if (o + len > buf.length) return null;
    names.push(dec.decode(buf.subarray(o, o + len)));
    o += len;
  }
  if (o + 2 > buf.length) return null;
  const actionCount = view.getUint16(o, true); o += 2;
  const actions: MatchAction[] = [];
  for (let i = 0; i < actionCount; i++) {
    if (o + 4 > buf.length) return null;
    const weaponIdx = buf[o++];
    const angleDeg = buf[o++];
    const power = view.getUint16(o, true); o += 2;
    if (weaponIdx >= WEAPONS.length) return null;
    actions.push({ weaponIdx, angleDeg, power });
  }
  return { version, seed, matchTarget, playerCount, names, actions };
}

/** Build a full URL for sharing, anchored at the current page origin. */
export function buildMatchUrl(token: string): string {
  const u = new URL(window.location.href);
  // Drop existing query/hash to keep links short
  u.search = '';
  u.hash = '';
  u.searchParams.set('m', token);
  return u.toString();
}

/** Parse the match token from the current URL, if any. */
export function readMatchTokenFromUrl(): string | null {
  try {
    const u = new URL(window.location.href);
    return u.searchParams.get('m');
  } catch {
    return null;
  }
}

// --- base64url helpers (no dependencies) ---

function bytesToBase64Url(buf: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
  const b64 = btoa(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  const bin = atob(b64 + pad);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf;
}
